import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ModuleProps } from '../types';
import AppHeader from '../AppHeader';
import { useLanguage } from '../LanguageContext';
import { el, en } from '../i18n/modules';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3000';

const ProjectSpecs: React.FC<ModuleProps> = ({ project, user, token, onBack, canEdit, onLogout, onProfileEdit}) => {
  const { language } = useLanguage();
  const M = language === 'en' ? en : el;
  const L = M.projectSpecs;
  const C = M.common;
  const [specs, setSpecs] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [gmailStatus, setGmailStatus] = useState<any>(null);
  const [emailEnabled, setEmailEnabled] = useState(false);
  const [projectEmail, setProjectEmail] = useState('');
  const [emailInput, setEmailInput] = useState('');
  const [savingEmail, setSavingEmail] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showDisconnectModal, setShowDisconnectModal] = useState(false);
  const [collapsed, setCollapsed] = useState({ basic: false, ai: false, email: false, sources: false });
  const toggle = (key: keyof typeof collapsed) => setCollapsed(p => ({ ...p, [key]: !p[key] }));
  const headers = { Authorization: `Bearer ${token}` };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const [specsRes, gmailRes] = await Promise.allSettled([
        axios.get(`${API}/advisor/${project.id}/specs`, { headers }),
        axios.get(`${API}/auth/gmail/status?projectId=${project.id}`, { headers }),
      ]);
      let savedProjectEmail = '';
      if (specsRes.status === 'fulfilled') {
        const data = specsRes.value.data;
        setSpecs(data);
        setEmailEnabled(!!data?.email_enabled);
        savedProjectEmail = data?.project_email || '';
        setProjectEmail(savedProjectEmail);
        setEmailInput(savedProjectEmail);
      }
      if (gmailRes.status === 'fulfilled') {
        const gmail = gmailRes.value.data;
        setGmailStatus(gmail);
        // Auto-save gmail_user as project_email if not already set
        if (gmail?.connected && gmail.gmail_user && !savedProjectEmail) {
          await axios.put(`${API}/advisor/${project.id}/email-settings`,
            { project_email: gmail.gmail_user },
            { headers }
          );
          setProjectEmail(gmail.gmail_user);
          setEmailInput(gmail.gmail_user);
        }
      }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const handleToggleEmail = async () => {
    setSavingEmail(true);
    try {
      const newVal = !emailEnabled;
      const emailToUse = gmailStatus?.connected ? (gmailStatus.gmail_user || emailInput) : emailInput;
      await axios.put(
        `${API}/advisor/${project.id}/email-settings`,
        { email_enabled: newVal, project_email: newVal ? emailToUse : null },
        { headers }
      );
      setEmailEnabled(newVal);
      if (!newVal) setProjectEmail('');
      else setProjectEmail(emailToUse);
    } catch (err) { console.error(err); }
    finally { setSavingEmail(false); }
  };

  const handleSaveEmail = async () => {
    if (!emailInput.trim()) return;
    setSavingEmail(true);
    try {
      await axios.put(
        `${API}/advisor/${project.id}/email-settings`,
        { email_enabled: true, project_email: emailInput },
        { headers }
      );
      setProjectEmail(emailInput);
      setEmailEnabled(true);
    } catch (err) { console.error(err); }
    finally { setSavingEmail(false); }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(projectEmail);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const formatDate = (d?: string) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString(C.dateLocale, { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const InfoRow = ({ label, value, highlight }: { label: string; value: any; highlight?: boolean }) => (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid #f3f4f6' }}>
      <span style={{ fontSize: '13px', color: '#6b7280', fontWeight: '500' }}>{label}</span>
      <span style={{ fontSize: '13px', color: highlight ? '#4f46e5' : '#1a1a2e', fontWeight: highlight ? '600' : '400' }}>{value}</span>
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f0f2f5' }}>
      <AppHeader user={user} onHome={onBack} onProfileEdit={onProfileEdit} onLogout={onLogout} />

      <div style={{ padding: '16px 32px', backgroundColor: 'white', borderBottom: '1px solid #e5e7eb', fontSize: '14px', position: 'sticky', top: 64, zIndex: 99 }}>
        <span style={{ color: '#4f46e5', cursor: 'pointer', fontWeight: '500' }} onClick={onBack}>{C.myProjects}</span>
        <span style={{ color: '#9ca3af', margin: '0 8px' }}>/</span>
        <span style={{ color: '#4f46e5', cursor: 'pointer', fontWeight: '500' }} onClick={onBack}>{project.name}</span>
        <span style={{ color: '#9ca3af', margin: '0 8px' }}>/</span>
        <span style={{ color: '#374151', fontWeight: '500' }}>Project Specs</span>
      </div>

      <div style={{ padding: '32px 48px', boxSizing: 'border-box' as const, height: 'calc(100vh - 130px)', display: 'flex', flexDirection: 'column' }}>
        <h2 style={{ fontSize: '24px', fontWeight: '600', color: '#1a1a2e', margin: '0 0 20px', flexShrink: 0 }}>Project Specs</h2>
        {loading ? <p style={{ color: '#666' }}>{C.loading}</p> : (() => {
          const sourcesWithActive = L.sources.map((s, i) => ({
            ...s, active: i === 0 ? !!specs?.ai_analysis : i === 4 ? emailEnabled && !!gmailStatus?.connected : true,
          }));
          const activeSourceCount = sourcesWithActive.filter(s => s.active).length;

          const SectionHeader = ({ title, k, preview }: { title: string; k: keyof typeof collapsed; preview: React.ReactNode }) => (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }} onClick={() => toggle(k)}>
              <div>
                <h3 style={{ fontSize: '13px', fontWeight: '700', color: '#4f46e5', margin: '0 0 4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{title}</h3>
                {collapsed[k] && <div style={{ fontSize: '12px', color: '#6b7280' }}>{preview}</div>}
              </div>
              <span style={{ fontSize: '11px', color: '#d1d5db', flexShrink: 0, marginLeft: '12px' }}>{collapsed[k] ? '▶' : '▼'}</span>
            </div>
          );

          return (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gridTemplateRows: '1fr 1fr', gap: '20px', flex: 1, minHeight: 0 }}>
              {/* Βασικά Στοιχεία */}
              <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '20px 24px', boxShadow: '0 1px 8px rgba(0,0,0,0.06)', display: 'flex', flexDirection: 'column', overflow: 'auto' }}>
                <SectionHeader title={L.sectionBasic} k="basic"
                  preview={<span>{specs?.name || project.name} · <span style={{ color: '#4f46e5', fontWeight: '600' }}>{specs?.status === 'active' ? L.fieldStatusActive : specs?.status}</span></span>} />
                {!collapsed.basic && (
                  <div style={{ marginTop: '14px' }}>
                    <InfoRow label={L.fieldProjectName} value={specs?.name || project.name} />
                    <InfoRow label={L.fieldStatus} value={specs?.status === 'active' ? L.fieldStatusActive : specs?.status} highlight />
                    <InfoRow label={L.fieldCreatedAt} value={formatDate(specs?.created_at)} />
                  </div>
                )}
              </div>

              {/* AI Status */}
              <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '20px 24px', boxShadow: '0 1px 8px rgba(0,0,0,0.06)', display: 'flex', flexDirection: 'column', overflow: 'auto' }}>
                <SectionHeader title={L.sectionAI} k="ai"
                  preview={<span>{specs?.ai_analysis ? L.fieldAIDone : L.fieldAINotRun} · {L.fieldTranscripts(specs?.transcripts_count || 0)}</span>} />
                {!collapsed.ai && (
                  <div style={{ marginTop: '14px' }}>
                    <InfoRow label={L.fieldAIAnalysis} value={specs?.ai_analysis ? L.fieldAIDone : L.fieldAINotRun} highlight={!!specs?.ai_analysis} />
                    <InfoRow label="Transcripts" value={L.fieldTranscripts(specs?.transcripts_count || 0)} />
                    <InfoRow label={L.fieldPendingInsights} value={specs?.pending_insights > 0 ? <span style={{ color: '#f59e0b', fontWeight: '600' }}>{L.fieldPendingCount(specs.pending_insights)}</span> : L.fieldPendingNone} />
                  </div>
                )}
              </div>

              {/* Email Integration — μόνο αν canEdit */}
              {canEdit && (
                <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '20px 24px', boxShadow: '0 1px 8px rgba(0,0,0,0.06)', display: 'flex', flexDirection: 'column', overflow: 'auto' }}>
                  <SectionHeader title={L.sectionEmail} k="email"
                    preview={gmailStatus?.connected
                      ? <span><span style={{ color: '#22c55e', fontWeight: '600' }}>● {L.gmailConnected}</span>{gmailStatus.gmail_user ? ` · ${gmailStatus.gmail_user}` : ''}</span>
                      : <span style={{ color: '#f59e0b' }}>⚠ {L.gmailNotConnected}</span>} />
                  {!collapsed.email && (
                    <div style={{ marginTop: '14px' }}>
                      {!gmailStatus?.connected ? (
                        <div style={{ backgroundColor: '#fff7ed', borderRadius: '10px', padding: '16px', border: '1px solid #fed7aa', marginBottom: '16px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
                            <span style={{ fontSize: '20px' }}>⚠️</span>
                            <div>
                              <div style={{ fontSize: '13px', fontWeight: '600', color: '#92400e' }}>{L.gmailNotConnected}</div>
                              <div style={{ fontSize: '12px', color: '#b45309' }}>{L.gmailConnectWarning}</div>
                            </div>
                          </div>
                          <a href={`${API}/auth/gmail/connect?project=${project.id}`} style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', backgroundColor: '#4f46e5', color: 'white', padding: '8px 16px', borderRadius: '8px', fontSize: '13px', fontWeight: '600', textDecoration: 'none' }}>
                            {L.gmailConnect}
                          </a>
                        </div>
                      ) : (
                        <div style={{ backgroundColor: '#f0fdf4', borderRadius: '8px', padding: '10px 14px', border: '1px solid #bbf7d0', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#22c55e', flexShrink: 0 }} />
                          <span style={{ fontSize: '13px', color: '#166534', fontWeight: '500' }}>{L.gmailConnected}</span>
                          {gmailStatus.gmail_user && <span style={{ fontSize: '12px', color: '#9ca3af' }}>({gmailStatus.gmail_user})</span>}
                          {gmailStatus.polling && <span style={{ fontSize: '11px', backgroundColor: '#dcfce7', color: '#166534', padding: '2px 8px', borderRadius: '999px' }}>{L.gmailPolling}</span>}
                          <button onClick={() => setShowDisconnectModal(true)} style={{ marginLeft: 'auto', fontSize: '11px', padding: '3px 10px', borderRadius: '6px', border: '1px solid #bbf7d0', backgroundColor: 'white', color: '#6b7280', cursor: 'pointer' }}>{L.gmailDisconnect}</button>
                        </div>
                      )}
                      <div style={{ backgroundColor: '#fafafa', borderRadius: '10px', padding: '16px', border: '1px solid #e5e7eb' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <span style={{ fontSize: '20px' }}>📧</span>
                            <div>
                              <div style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>{L.emailLabel}</div>
                              {gmailStatus?.connected && gmailStatus.gmail_user
                                ? <div style={{ fontSize: '13px', color: '#4f46e5', fontWeight: '500', marginTop: '2px', fontFamily: 'monospace' }}>{gmailStatus.gmail_user}</div>
                                : <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '2px' }}>{L.emailHint}</div>}
                            </div>
                          </div>
                          {gmailStatus?.connected && (
                            <button onClick={handleToggleEmail} disabled={savingEmail} style={{ width: '44px', height: '24px', borderRadius: '12px', border: 'none', cursor: 'pointer', backgroundColor: emailEnabled ? '#4f46e5' : '#d1d5db', position: 'relative', transition: 'background-color 0.2s', flexShrink: 0 }}>
                              <div style={{ position: 'absolute', top: '2px', left: emailEnabled ? '22px' : '2px', width: '20px', height: '20px', borderRadius: '50%', backgroundColor: 'white', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Πηγές Δεδομένων AI */}
              <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '20px 24px', boxShadow: '0 1px 8px rgba(0,0,0,0.06)', display: 'flex', flexDirection: 'column', overflow: 'auto' }}>
                <SectionHeader title={L.sectionSources} k="sources"
                  preview={<span>{activeSourceCount} / {sourcesWithActive.length} πηγές ενεργές</span>} />
                {!collapsed.sources && (
                  <div style={{ marginTop: '14px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {sourcesWithActive.map((source, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 12px', backgroundColor: '#fafafa', borderRadius: '8px', border: '1px solid #f3f4f6' }}>
                        <span style={{ fontSize: '18px', flexShrink: 0 }}>{source.icon}</span>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: '13px', fontWeight: '500', color: '#374151' }}>{source.label}</div>
                          <div style={{ fontSize: '11px', color: '#9ca3af' }}>{source.desc}</div>
                        </div>
                        <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: source.active ? '#22c55e' : '#d1d5db', flexShrink: 0 }} />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })()}
      </div>

      {showDisconnectModal && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ backgroundColor: 'white', borderRadius: '16px', padding: '32px', width: '100%', maxWidth: '400px', boxShadow: '0 20px 60px rgba(0,0,0,0.15)' }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: '24px' }}>
              <div style={{ width: '56px', height: '56px', borderRadius: '50%', backgroundColor: '#fef2f2', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '16px' }}>
                <span style={{ fontSize: '24px' }}>🔌</span>
              </div>
              <div style={{ fontSize: '18px', fontWeight: '700', color: '#111827', marginBottom: '6px' }}>{language === 'en' ? 'Disconnect Gmail' : 'Αποσύνδεση Gmail'}</div>
              <div style={{ fontSize: '13px', color: '#6b7280', textAlign: 'center' }}>{language === 'en' ? 'Email polling will stop for this project.' : 'Το polling email θα σταματήσει για αυτό το project.'}</div>
            </div>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={() => setShowDisconnectModal(false)}
                style={{ flex: 1, padding: '10px', borderRadius: '8px', border: '1px solid #e5e7eb', backgroundColor: 'white', fontSize: '14px', fontWeight: '500', color: '#374151', cursor: 'pointer' }}
              >
                {C.cancel}
              </button>
              <button
                onClick={async () => {
                  await axios.delete(`${API}/auth/gmail/disconnect?projectId=${project.id}`, { headers });
                  await axios.put(`${API}/advisor/${project.id}/email-settings`, { email_enabled: false, project_email: null }, { headers });
                  setGmailStatus({ connected: false, polling: false, gmail_user: '' });
                  setEmailEnabled(false);
                  setProjectEmail('');
                  setEmailInput('');
                  setShowDisconnectModal(false);
                }}
                style={{ flex: 1, padding: '10px', borderRadius: '8px', border: 'none', backgroundColor: '#ef4444', fontSize: '14px', fontWeight: '600', color: 'white', cursor: 'pointer' }}
              >
                {language === 'en' ? 'Disconnect' : 'Αποσύνδεση'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectSpecs;
